/**
 * iter350 — Confier le dossier à un collègue (le dossier reste unique).
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SERVICES = [
  ["mecanique", "Mécanique"], ["carrosserie", "Carrosserie"], ["peinture", "Peinture"],
  ["pneus", "Pneus"], ["diagnostic", "Diagnostic"], ["electrique", "Électricité"], ["lavage", "Lavage"],
];

export const ConfierDialog = ({ open, onClose, orId, onDone }) => {
  const [collegues, setCollegues] = useState([]);
  const [cible, setCible] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/atelier/collegues")
      .then(({ data }) => setCollegues((data.items || []).filter((c) => !c.moi)))
      .catch(() => setCollegues([]));
  }, [open]);

  const confier = async () => {
    if (!cible) { toast.error("Choisis un collègue"); return; }
    setBusy(true);
    try {
      await api.post(`/atelier/travail/${orId}/confier`, {
        mecanicien_id: cible, service: service || null, message: message.trim() || null,
      });
      toast.success("Dossier confié");
      setCible(""); setService(""); setMessage("");
      onDone?.();
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || d || "Transfert impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="confier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4 text-[#52a675]" /> Confier à un collègue
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Collaborateur</Label>
            <Select value={cible} onValueChange={setCible}>
              <SelectTrigger className="h-12" data-testid="confier-collegue">
                <SelectValue placeholder="Choisir un collègue" />
              </SelectTrigger>
              <SelectContent>
                {collegues.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`confier-collegue-${c.id}`}>{c.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Service</Label>
            <Select value={service} onValueChange={setService}>
              <SelectTrigger className="h-12" data-testid="confier-service">
                <SelectValue placeholder="Type de travail" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map(([v, l]) => (
                  <SelectItem key={v} value={v} data-testid={`confier-service-${v}`}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Message</Label>
            <Textarea
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex. Contrôler les défauts ADAS après choc avant"
              rows={3} className="resize-none" data-testid="confier-message"
            />
          </div>
          <p className="text-[11px] text-gray-500">
            Tu restes sur le dossier : tes heures, ton historique et les messages te restent accessibles.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="confier-annuler">Annuler</Button>
          <Button onClick={confier} disabled={busy} className="bg-[#52a675] hover:bg-[#2d5f3f]" data-testid="confier-valider">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfierDialog;
